import { Box } from "@mui/material"
import { CheckCircleFilledRounded, DoNotDisturbOnFilledRounded } from "@mui-symbols-material/w300"
import type { ColumnDef } from "@tanstack/react-table"
import dayjs from "dayjs"

import { OrderStatusChip } from "../../PurchaseOrder/components/OrderStatusChip"
import { ShipStatusFlatChip } from "../../PurchaseOrder/components/ShipStatusFlatChip"
import { PurchaseOrderActions } from "../components/PurchaseOrderActions"

import StyledChip from "@/components/common/Chip/Chip"
import DateChip from "@/components/DateChip"
import { PurchaseOrderNumberCell } from "@/components/table"
import { UOM_RENDER_PRECISION } from "@/constants/formats"
import type { Order, OrderStatus, ShipStatus, UnitOfMeasurement } from "@/graphql/codegen/graphql"
import { formatNumber } from "@/utils/number"
import { getTotalSummaryPurchaseOrder } from "@/utils/purchaseOrder"

export const getTableColumns = ({
  units,
  onUploadClick,
  canUpload,
}: {
  units: UnitOfMeasurement[]
  onUploadClick: (orderId: string) => void
  canUpload: boolean
}): ColumnDef<Order>[] => [
  {
    header: "Supplier Name",
    accessorKey: "companyName",
    accessorFn: (row) => row.sourceOrderLocationDetail?.location?.name,
    cell: ({ row }) =>
      row.original.sourceLocationAssociationDetail?.companyLocationAssociation?.companyDetail?.company?.name || "-",
  },
  {
    header: "PO No.",
    accessorKey: "orderId",
    cell: ({ row }) => <PurchaseOrderNumberCell order={row.original} />,
  },
  {
    header: "Status",
    accessorKey: "status",
    cell: ({ row }) => <OrderStatusChip status={row.original.status as OrderStatus} />,
  },
  {
    header: "Ship Status",
    accessorKey: "shipStatus",
    cell: ({ row }) => <ShipStatusFlatChip shipStatus={row.original.shipStatus as ShipStatus} />,
  },
  {
    header: "Requested",
    accessorKey: "requestedDeliveryDate",
    cell: ({ getValue }) => {
      const date = getValue<string>()
      return (
        <StyledChip
          label={
            <span>
              {date && <strong>By</strong>} {date ? dayjs(date).format("MM/DD/YYYY") : "--/--/----"}
            </span>
          }
          className='text-tertiary-base'
          color='primary'
        />
      )
    },
  },
  {
    header: "Expected",
    accessorKey: "expectedDeliveryDate",
    cell: ({ getValue }) => {
      const date = getValue<string>()
      return <DateChip date={date} className='text-tertiary-base' color='primary' />
    },
  },
  {
    header: "Order Total",
    cell: ({ row }) => {
      const { totalAmount, totalWeight } = getTotalSummaryPurchaseOrder({ orderLines: row?.original?.orderLines ?? [] })
      const usUom = units.find((u) => u.id === totalWeight.us.uomId)
      const metricUom = units.find((u) => u.id === totalWeight.metric.uomId)

      return (
        <Box>
          {formatNumber(totalWeight.us.amount, UOM_RENDER_PRECISION)} {usUom?.name} (
          {formatNumber(totalWeight.metric.amount, UOM_RENDER_PRECISION)} {metricUom?.name})
          <br />
          <strong>${formatNumber(totalAmount, UOM_RENDER_PRECISION)}</strong>
        </Box>
      )
    },
    enableSorting: false,
  },
  {
    header: "Results",
    cell: ({ row }) =>
      row.original.withTestResults ? (
        <CheckCircleFilledRounded className='text-tertiary-green-500' />
      ) : (
        <DoNotDisturbOnFilledRounded className='text-gray-500' />
      ),
    enableSorting: false,
  },
  {
    id: "expand",
    header: "",
    cell: ({ row }) => (
      <PurchaseOrderActions
        row={row}
        purchaseOrderId={row.original.orderId}
        onUploadClick={onUploadClick}
        canUpload={canUpload}
        orderType={row.original.type}
      />
    ),
    enableSorting: false,
  },
]
